import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { UpgradePro } from '../UpgradePro';
import type { ProPlan } from '../../types';
import * as authService from '../../services/authService';
import { SpinnerIcon } from '../icons/SpinnerIcon';
import { PasswordInput } from '../PasswordInput';
import { useToast } from '../../hooks/useToast';

export const AccountPanel: React.FC = () => {
  const { user, setUser } = useAuth();
  const { showToast } = useToast();

  const [isSwitchingPlan, setIsSwitchingPlan] = useState(false);
  const [switchSuccessMessage, setSwitchSuccessMessage] = useState<string | null>(null);

  const prevPlanRef = useRef<ProPlan | undefined>(user?.plan);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isNewPasswordStrong, setIsNewPasswordStrong] = useState(false);
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordChangeError, setPasswordChangeError] = useState<string | null>(null);
  const [isPasswordChangeLoading, setIsPasswordChangeLoading] = useState(false);

  useEffect(() => {
    if (user?.plan && user.plan !== prevPlanRef.current && prevPlanRef.current) {
      setSwitchSuccessMessage(`Your plan has been switched to ${user.plan}.`);
      setIsSwitchingPlan(false);
      const timer = setTimeout(() => setSwitchSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
    prevPlanRef.current = user?.plan;
  }, [user]);

  if (!user) {
    return null;
  }

  const handleUpgradeSuccess = async (plan: ProPlan) => {
    if (!user) return;
    const updatedUser = await authService.updateUserProStatus(user.uid, true, plan);
    if (updatedUser) {
      setUser(updatedUser);
    }
    setIsSwitchingPlan(false);
  };

  const handlePasswordChangeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordChangeError(null);
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      setPasswordChangeError("All fields are required.");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setPasswordChangeError("New passwords do not match.");
      return;
    }
    if (!isNewPasswordStrong) {
      setPasswordChangeError("New password is not strong enough. Please choose a password that is 'Good' or 'Strong'.");
      return;
    }
    setIsPasswordChangeLoading(true);
    try {
      await authService.changeUserPassword(currentPassword, newPassword);
      showToast("Password updated successfully!");
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setPasswordChangeError(null);
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.includes('auth/wrong-password') || err.message.includes('auth/requires-recent-login')) {
          setPasswordChangeError("Incorrect current password. For security, you may need to log out and log back in before changing your password.");
        } else {
          setPasswordChangeError(err.message);
        }
      } else {
        setPasswordChangeError("An unknown error occurred.");
      }
    } finally {
      setIsPasswordChangeLoading(false);
    }
  };

  return (
    <div>
      {switchSuccessMessage && (
        <div className="bg-green-50 border-l-4 border-green-500 text-green-700 p-4 rounded-md mb-6" role="alert">
          <p className="font-bold">Success!</p>
          <p>{switchSuccessMessage}</p>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-50 rounded-lg p-4 text-center border border-gray-200">
          <p className="text-sm text-[#6B7280]">Generations Used</p>
          <p className="text-2xl font-bold text-[#1F2937]">{user.stats?.generationsUsed ?? 0}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4 text-center border border-gray-200">
          <p className="text-sm text-[#6B7280]">Total Names Generated</p>
          <p className="text-2xl font-bold text-[#1F2937]">{user.stats?.namesGenerated ?? 0}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4 text-center border border-gray-200">
          <p className="text-sm text-[#6B7280]">Total PDFs Exported</p>
          <p className="text-2xl font-bold text-[#1F2937]">{user.stats?.pdfsExported ?? 0}</p>
        </div>
      </div>
      {user.isPro ? (
        isSwitchingPlan ? (
          <div className="my-6">
            <UpgradePro
              onUpgradeSuccess={handleUpgradeSuccess}
              title="Switch Your Plan"
              description="Select a new subscription plan below."
            />
            <button onClick={() => setIsSwitchingPlan(false)} className="mt-4 w-full text-sm text-center text-[#6B7280] hover:underline">Cancel</button>
          </div>
        ) : (
          <div className="bg-gray-50 rounded-lg p-6 mb-6 text-left space-y-4 border border-gray-200">
            <div>
              <p className="text-sm text-[#6B7280]">Status</p>
              <p className="text-lg font-semibold text-[#4F46E5]">Pro User</p>
            </div>
            {user.plan && (
              <div>
                <p className="text-sm text-[#6B7280]">Current Plan</p>
                <p className="font-medium text-[#1F2937]">{user.plan}</p>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <a href="https://www.paypal.com/myaccount/autopay/" target="_blank" rel="noopener noreferrer" className="w-full text-center py-2 px-4 bg-gray-200 hover:bg-gray-300 text-[#1F2937] font-semibold rounded-lg shadow-sm transition-colors duration-300">Manage Subscription</a>
              <button onClick={() => setIsSwitchingPlan(true)} className="w-full py-2 px-4 bg-gray-200 hover:bg-gray-300 text-[#1F2937] font-semibold rounded-lg shadow-sm transition-colors duration-300">Switch Plan</button>
            </div>
          </div>
        )
      ) : (
        <div className="mb-6">
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-center border border-gray-200">
            <p className="text-sm text-[#6B7280]">Your Status</p>
            <p className="text-lg font-semibold text-[#1F2937]">Free User</p>
          </div>
          <UpgradePro onUpgradeSuccess={handleUpgradeSuccess} title="Upgrade to Pro" description="Unlock unlimited generations and all pro features." />
        </div>
      )}
      <div className="mt-8">
        <h2 className="text-xl font-bold text-[#1F2937] mb-4">White-label Reporting</h2>
        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-[#1F2937] mb-2">Custom Consultant Logo</h3>
          <p className="text-sm text-gray-600 mb-4">Upload your agency or consultant logo to appear on generated PDF reports instead of the BrandGenius logo. We'll automatically resize it for the PDF. (Max 5MB)</p>
          {user.isPro ? (
            <div className="space-y-4">
              {user.customLogoBase64 ? (
                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-2">Current Logo:</p>
                  <div className="p-4 bg-white border border-gray-200 rounded-md inline-block">
                    <img src={user.customLogoBase64} alt="Custom Logo" className="max-h-16 object-contain" />
                  </div>
                  <div className="mt-2">
                    <button 
                      onClick={async () => {
                        const updated = await authService.updateUserCustomLogo(user.uid, null);
                        if (updated) setUser(updated);
                        showToast("Custom logo removed.");
                      }}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      Remove Logo
                    </button>
                  </div>
                </div>
              ) : null}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload New Logo</label>
                <input 
                  type="file" 
                  accept="image/png, image/jpeg, image/jpg" 
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    
                    if (file.size > 5 * 1024 * 1024) {
                      showToast("Image too large. Please upload an image under 5MB.");
                      return;
                    }
                    
                    const reader = new FileReader();
                    reader.onload = async (event) => {
                      const base64String = event.target?.result as string;
                      
                      // Resize and compress the image
                      const img = new Image();
                      img.src = base64String;
                      img.onload = async () => {
                        const canvas = document.createElement('canvas');
                        let width = img.width;
                        let height = img.height;
                        const MAX_WIDTH = 500;
                        const MAX_HEIGHT = 150;
                        
                        if (width > MAX_WIDTH || height > MAX_HEIGHT) {
                          const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
                          width = width * ratio;
                          height = height * ratio;
                        }
                        
                        canvas.width = width;
                        canvas.height = height;
                        const ctx = canvas.getContext('2d');
                        
                        // Draw with white background in case it's transparent and converted to JPEG (though we save as PNG)
                        // But since we use image/png, transparency is preserved.
                        ctx?.drawImage(img, 0, 0, width, height);
                        
                        const compressedBase64 = canvas.toDataURL('image/png', 0.8);
                        
                        try {
                          // Double check compressed size isn't crazy (base64 string length)
                          // 1 MB is approx 1.37 million characters
                          if (compressedBase64.length > 500 * 1024) {
                             showToast("Compressed image is still too large. Try a simpler image.");
                             return;
                          }
                          const updated = await authService.updateUserCustomLogo(user.uid, compressedBase64);
                          if (updated) setUser(updated);
                          showToast("Custom logo uploaded successfully!");
                        } catch (err) {
                          console.error("Error saving logo", err);
                          showToast("Failed to save logo.");
                        }
                      };
                    };
                    reader.readAsDataURL(file);
                  }}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100" 
                />
              </div>
            </div>
          ) : (
            <div className="bg-white p-4 rounded-md border border-gray-200 text-center">
              <p className="text-gray-500 mb-2">Upgrade to Pro to add your own logo to PDF reports.</p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-8">
        <h2 className="text-xl font-bold text-[#1F2937] mb-4">Account Settings</h2>
        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <form onSubmit={handlePasswordChangeSubmit} className="space-y-4 max-w-md">
            <h3 className="text-lg font-semibold text-[#1F2937]">Change Password</h3>
            {passwordChangeError && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">{passwordChangeError}</div>}
            <PasswordInput label="Current Password" id="current-password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
            <PasswordInput label="New Password" id="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} onStrengthChange={setIsNewPasswordStrong} showStrengthMeter required minLength={8} />
            <PasswordInput label="Confirm New Password" id="confirm-new-password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} required />
            <div className="pt-2">
              <button type="submit" disabled={isPasswordChangeLoading} className="w-full sm:w-auto flex justify-center items-center gap-2 py-2 px-6 bg-[#4F46E5] hover:bg-[#6366F1] text-white font-bold rounded-lg shadow-md transition-all duration-300 disabled:opacity-50">{isPasswordChangeLoading ? <><SpinnerIcon /> Updating...</> : 'Update Password'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
